/**
 * a_cart.js — カートページ専用（店舗受取・ルート種別・チェックアウト UI）
 */

// =============================================================================
// Shopify API 直接送信関数
// =============================================================================
const DELIVERY_PATTERN_NUMBERS = { '送料込み': '1', '常温便': '2', 'クール便': '3' };
let deliveryStateSaveQueue = Promise.resolve();

function getDeliveryPatternState(patternName) {
  const options = Array.from(document.querySelectorAll('.js-delivery-options')).find(
    (element) => element.getAttribute('data-delivery-pattern') === patternName
  );

  if (!options) {
    return { dateEnabled: false, timeEnabled: false, day: '', time: '' };
  }

  const dateEnabled = options.getAttribute('data-delivery-date-enabled') === 'true';
  const timeEnabled = options.getAttribute('data-delivery-time-enabled') === 'true';
  const dateControl = options.querySelector(
    '.js-group-delivery-date, input[type="hidden"][name="properties[配送希望日]"]'
  );
  const timeControl = options.querySelector('.js-group-delivery-time');

  return {
    dateEnabled,
    timeEnabled,
    day: dateEnabled && dateControl ? dateControl.value : '',
    time: timeEnabled && timeControl ? timeControl.value : '',
  };
}

function syncDeliveryLineInputsFromPatternControls() {
  document.querySelectorAll('tr.a_cart__item[data-delivery-pattern]').forEach((row) => {
    const patternName = row.getAttribute('data-delivery-pattern') || '';
    const state = getDeliveryPatternState(patternName);
    row.querySelectorAll('.js-hidden-delivery-date').forEach((input) => {
      input.value = state.day;
    });
    row.querySelectorAll('.js-hidden-delivery-time').forEach((input) => {
      input.value = state.time;
    });
  });
}

async function persistDeliveryStateViaAPI() {
  try {
    syncDeliveryLineInputsFromPatternControls();

    const cartRes = await fetch('/cart.js');
    const cartData = await cartRes.json();
    const attributes = {};

    // --- 1. カート全体の attributes（Shopify Flow連携用）を収集 ---
    for (const patternName in DELIVERY_PATTERN_NUMBERS) {
      const num = DELIVERY_PATTERN_NUMBERS[patternName];
      const state = getDeliveryPatternState(patternName);
      const productIds = document.querySelector(
        `input[name="attributes[delivery_pattern${num}_productID]"]`
      )?.value || '';
      const desiredAttributes = {
        [`delivery_pattern${num}_day`]: state.day,
        [`delivery_pattern${num}_time`]: state.time,
        [`delivery_pattern${num}_productID`]: productIds,
      };

      for (const key in desiredAttributes) {
        const currentValue = cartData.attributes?.[key] || '';
        if (currentValue !== desiredAttributes[key]) {
          attributes[key] = desiredAttributes[key];
        }
      }
    }

    if (Object.keys(attributes).length > 0) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });
    }

    // --- 2. 各行（発送商品）の properties（チェックアウト画面表示用）を更新 ---
    const deliveryItems = document.querySelectorAll('tr.a_cart__item[data-delivery-pattern]');
    if (deliveryItems.length > 0) {
      const changeUrl = typeof routes !== "undefined" && routes.cart_change_url ? routes.cart_change_url : "/cart/change.js";

      for (const row of deliveryItems) {
        const lineIndex = parseInt(row.getAttribute('data-index'), 10);
        if (!lineIndex || isNaN(lineIndex)) continue;

        const item = cartData.items[lineIndex - 1];
        if (!item) continue;

        const state = getDeliveryPatternState(row.getAttribute('data-delivery-pattern') || '');
        const newDay = state.day;
        const newTime = state.time;
        const prevProps = item.properties && typeof item.properties === 'object' && !Array.isArray(item.properties) ? item.properties : {};

        if ((prevProps["配送希望日"] || '') === newDay && (prevProps["配送時間帯"] || '') === newTime) {
          continue;
        }

        const mergedProps = { ...prevProps };
        if (newDay) mergedProps["配送希望日"] = newDay;
        else delete mergedProps["配送希望日"];
        if (newTime) mergedProps["配送時間帯"] = newTime;
        else delete mergedProps["配送時間帯"];

        await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineIndex,
            quantity: item.quantity,
            properties: mergedProps
          })
        });
      }
    }
  } catch (err) {
    console.error('API Error:', err);
  }
}

function saveFlowAttributesViaAPI() {
  deliveryStateSaveQueue = deliveryStateSaveQueue
    .catch(() => {})
    .then(() => persistDeliveryStateViaAPI());
  return deliveryStateSaveQueue;
}

// =============================================================================
// トップレベル：ユーティリティ
// =============================================================================

function cartPageIsCustomerLoggedIn() {
  return typeof window !== "undefined" && typeof window.isCustomer === "boolean" ? window.isCustomer : false;
}

function getSectionInnerHTML(html, selector) {
  return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
}

function getCartSectionsToRender() {
  const mainItems = document.getElementById("main-cart-items");
  const mainFooter = document.getElementById("main-cart-footer");
  if (!mainItems?.dataset?.id || !mainFooter?.dataset?.id) return [];
  return [
    { id: "main-cart-items", section: mainItems.dataset.id, selector: ".js-contents" },
    { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
    { id: "cart-live-region-text", section: "cart-live-region-text", selector: ".shopify-section" },
    { id: "main-cart-footer", section: mainFooter.dataset.id, selector: ".js-contents" },
  ];
}

function applyCartSectionsFromChangeResponse(parsedState) {
  if (!parsedState?.sections) return;
  getCartSectionsToRender().forEach((section) => {
    const html = parsedState.sections[section.section];
    if (!html) return;
    const elementToReplace =
      document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
    if (!elementToReplace) return;
    elementToReplace.innerHTML = getSectionInnerHTML(html, section.selector);
  });
  const cartFooter = document.getElementById("main-cart-footer");
  if (cartFooter) cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
  document.querySelector("cart-items")?.classList.toggle("is-empty", parsedState.item_count === 0);
}

const AGE_GATE_REMEMBER_IN_SESSION = false;
const AGE_GATE_SESSION_KEY = "isAgeConfirmed";

// =============================================================================
// CartManager
// =============================================================================

const CartManager = {
  _noteError: false,
  _ageGateCheckoutConfirmed: false,
  _ageGateTargetUrl: null,
  _pickupStoreMasterClones: null,
  _documentEventsBound: false,
  _deliveryStopHandler: null,
  _deliveryStopBoundForm: null,
  _quantityLimitBlurResyncTimers: new WeakMap(),

  init() {
    this.invalidatePickupMasterCache();
    this.initAgeGate();
    this.initPickupRequiredModal();
    this.bindDocumentEvents();
    this.bindDeliverySelectStopPropagation();

    this.sortPickupStores();
    this.ensurePickupMasterClones();
    this.filterPickupOptionsByCartItems(); 
    this.syncFooterDeliveryTextFromBridge();

    const areaSelect = document.getElementById("pickup-area");
    if (areaSelect && areaSelect.value) {
      this.onPickupAreaChange();
    }

    this.updateAllStates();
    this.renderPickupDates(this.getSelectedPickupStoreCd());
    this.validatePickupRequired(false);
    this.refreshCheckoutButton();

    this.clearPickupAttributesIfNoPickupItems();
    saveFlowAttributesViaAPI();
  },

  onCartItemsAjaxDone() {
    this.invalidatePickupMasterCache();
    this.sortPickupStores();
    this.ensurePickupMasterClones();
    this.filterPickupOptionsByCartItems();
    this.renderPickupDates(this.getSelectedPickupStoreCd());
    requestAnimationFrame(() => {
      this.bindDeliverySelectStopPropagation();
      this.syncFooterDeliveryTextFromBridge();
      this.updateAllStates();
      this.validatePickupRequired(false);
      this.refreshCheckoutButton();
      this.initAgeGate();
      this.initPickupRequiredModal();

      this.clearPickupAttributesIfNoPickupItems();
      saveFlowAttributesViaAPI();
    });
  },

  syncFooterDeliveryTextFromBridge() {
    const bridge = document.getElementById("CartFooterDeliveryBridge");
    const el = document.getElementById("CartFooterDeliveryText");
    if (!bridge || !el) return;
    const visible = bridge.getAttribute("data-visible") === "true";
    el.style.display = visible ? "" : "none";
  },

  bindDeliverySelectStopPropagation() {
    if (this._deliveryStopBound) return;
    this._deliveryStopBound = true;

    const handler = (e) => {
      const t = e.target;
      const nameAttr = t.getAttribute("name") || "";

      if (t.classList.contains("js-group-delivery-date") || t.classList.contains("js-group-delivery-time")) {
        e.stopPropagation();
        e.stopImmediatePropagation();

        const targetPattern = t.getAttribute("data-target-pattern");
        const newVal = t.value;
        const isDate = t.classList.contains("js-group-delivery-date");

        const targetRows = document.querySelectorAll(`tr.a_cart__item[data-delivery-pattern="${targetPattern}"]`);
        targetRows.forEach(row => {
          if (isDate) {
            row.querySelectorAll('.js-hidden-delivery-date').forEach(h => { h.value = newVal; });
          } else {
            row.querySelectorAll('.js-hidden-delivery-time').forEach(h => { h.value = newVal; });
          }
        });
      }
      else if (nameAttr === "properties[配送希望日]" || nameAttr === "properties[配送時間帯]") {
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        this.syncDeliveryLineProperties(t);
      }
    };
    document.addEventListener("change", handler, true);
  },

  syncDeliveryLineProperties(changedSelect) {
    const row = changedSelect.closest("tr.a_cart__item, tr.cart-item");
    if (!row) return;
    const line = parseInt(row.getAttribute("data-index"), 10);
    if (!Number.isFinite(line) || line < 1) return;

    if (changedSelect.dataset.isUpdating === "true") return;
    changedSelect.dataset.isUpdating = "true";

    const changedName = changedSelect.getAttribute("name");
    if (changedName === "properties[配送希望日]") {
      row.querySelectorAll('select[name="properties[配送希望日]"]').forEach((s) => {
        s.value = changedSelect.value;
      });
      row.querySelectorAll('input[type="hidden"][name="properties[配送希望日]"]').forEach((h) => {
        h.value = changedSelect.value;
      });
    } else if (changedName === "properties[配送時間帯]") {
      row.querySelectorAll('select[name="properties[配送時間帯]"]').forEach((s) => {
        s.value = changedSelect.value;
      });
    }

    const dateVal =
      row.querySelector('select[name="properties[配送希望日]"]')?.value ??
      row.querySelector('input[type="hidden"][name="properties[配送希望日]"]')?.value ??
      "";
    const timeVal = row.querySelector('select[name="properties[配送時間帯]"]')?.value ?? "";

    const cartUrl = typeof routes !== "undefined" && routes.cart_url ? `${routes.cart_url}.js` : "/cart.js";
    const changeUrl = typeof routes !== "undefined" && routes.cart_change_url ? routes.cart_change_url : "/cart/change.js";
    const fetchOpts = typeof fetchConfig === "function" ? fetchConfig() : { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" } };

    fetch(cartUrl)
      .then((r) => r.json())
      .then((cart) => {
        const item = cart.items[line - 1];
        if (!item) throw new Error("missing line");
        const prev = item.properties && typeof item.properties === "object" && !Array.isArray(item.properties) ? item.properties : {};
        if (prev["配送希望日"] === dateVal && prev["配送時間帯"] === timeVal) {
          return Promise.reject("NO_CHANGE");
        }
        const merged = { ...prev, 配送希望日: dateVal, 配送時間帯: timeVal };
        const sectionIds = getCartSectionsToRender().map((s) => s.section);
        const body = { line, quantity: item.quantity, properties: merged };
        if (sectionIds.length) {
          body.sections = sectionIds;
          body.sections_url = window.location.pathname;
        }
        return fetch(changeUrl, { ...fetchOpts, body: JSON.stringify(body) }).then((r) => r.text());
      })
      .then((text) => {
        if (!text) return;
        const parsedState = JSON.parse(text);
        if (parsedState.errors) {
          const errEl = document.getElementById("cart-errors");
          if (errEl) errEl.textContent = typeof parsedState.errors === "string" ? parsedState.errors : parsedState.message || JSON.stringify(parsedState.errors);
          return;
        }
        const errEl = document.getElementById("cart-errors");
        if (errEl) errEl.textContent = "";
        applyCartSectionsFromChangeResponse(parsedState);
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items", cartData: parsedState });
        }
      })
      .catch((err) => { if (err !== "NO_CHANGE") console.error(err); })
      .finally(() => { changedSelect.dataset.isUpdating = "false"; });
  },

  invalidatePickupMasterCache() {
    this._pickupStoreMasterClones = null;
    this._pickupAreaMasterClones = null;
  },

  ensurePickupMasterClones() {
    if (this._pickupStoreMasterClones && this._pickupAreaMasterClones) return;
    
    const storeSelect = document.getElementById("pickup-store");
    const storeCdInput = document.getElementById("pickup-store-cd");
    
    if (storeSelect && !this._pickupStoreMasterClones) {
      this._initialSelectedStoreName = storeSelect.value || "";
      this._initialSelectedStoreCd = storeCdInput ? storeCdInput.value : "";

      this._pickupStoreMasterClones = Array.from(storeSelect.querySelectorAll('option:not([value=""])')).map((opt) => {
        const clone = opt.cloneNode(true);
        clone.style.display = ""; 
        return clone;
      });
      storeSelect.innerHTML = '<option value="">店舗を選択してください</option>';
    }

    const areaSelect = document.getElementById("pickup-area");
    if (areaSelect && !this._pickupAreaMasterClones) {
      this._pickupAreaMasterClones = Array.from(areaSelect.querySelectorAll('option:not([value=""])')).map((opt) => {
        const clone = opt.cloneNode(true);
        clone.style.display = "";
        return clone;
      });
    }
  },

  filterPickupOptionsByCartItems() {
    const areaSelect = document.getElementById("pickup-area");
    if (!areaSelect) return;

    const cartItems = JSON.parse(document.getElementById("CartItemPickupData")?.textContent || "[]");
    const allowedStoreCodes = new Set();
    cartItems.forEach(item => {
      if (item.shop_codes && Array.isArray(item.shop_codes)) {
        item.shop_codes.forEach(code => {
          if (code) allowedStoreCodes.add(String(code).trim());
        });
      }
    });

    const allowedAreaCds = new Set();
    this.ensurePickupMasterClones();
    if (this._pickupStoreMasterClones) {
      this._pickupStoreMasterClones.forEach(opt => {
        const storeCd = opt.getAttribute("data-store-cd")?.trim();
        const areaCd = opt.getAttribute("data-area")?.trim();
        if (allowedStoreCodes.has(storeCd) && areaCd) {
          allowedAreaCds.add(areaCd);
        }
      });
    }

    const currentSelectedArea = areaSelect.value;
    areaSelect.innerHTML = '<option value="" hidden>エリアを選択してください</option>';

    if (this._pickupAreaMasterClones) {
      this._pickupAreaMasterClones.forEach(opt => {
        const areaCd = opt.getAttribute("data-area-cd");
        if (areaCd && allowedAreaCds.has(areaCd)) {
          const clone = opt.cloneNode(true);
          if (clone.value === currentSelectedArea) {
            clone.selected = true;
          }
          areaSelect.appendChild(clone);
        }
      });
    }
  },

  getSelectedPickupStoreCd() {
    const storeSelect = document.getElementById("pickup-store");
    if (!storeSelect || storeSelect.selectedIndex < 0) return "";
    return storeSelect.options[storeSelect.selectedIndex]?.getAttribute("data-store-cd")?.trim() || "";
  },

  initAgeGate() {
    const modal = document.getElementById("AgeGateModal");
    if (!modal) return;
    if (AGE_GATE_REMEMBER_IN_SESSION && sessionStorage.getItem(AGE_GATE_SESSION_KEY) === "true") {
      modal.remove();
      return;
    }
    if (modal.dataset.aCartAgeGateInit === "1") return;
    modal.dataset.aCartAgeGateInit = "1";
    modal.classList.remove("is-active");
    document.body.classList.remove("is-locked");

    const checkbox = document.getElementById("AgeCheck");
    const button = document.getElementById("AgeGateButton");

    if (button) button.innerText = "購入に進む";

    checkbox?.addEventListener("change", (e) => {
      if (button) button.disabled = !e.target.checked;
    });

    button?.addEventListener("click", () => {
      if (AGE_GATE_REMEMBER_IN_SESSION) {
        sessionStorage.setItem(AGE_GATE_SESSION_KEY, "true");
        modal.remove();
        document.body.classList.remove("is-locked");
      } else {
        this._ageGateCheckoutConfirmed = true;
        modal.classList.remove("is-active");
        document.body.classList.remove("is-locked");
        if (checkbox) checkbox.checked = false;
        if (button) button.disabled = true;
      }

      if (this._ageGateTargetUrl) {
        if (button) button.innerText = "処理中...";
        window.location.href = this._ageGateTargetUrl;
        return;
      }

      const cartForm = document.getElementById("cart");
      if (cartForm) {
        if (button) button.innerText = "処理中..."; 
        saveFlowAttributesViaAPI().then(() => {
          const checkoutInput = document.createElement("input");
          checkoutInput.type = "hidden";
          checkoutInput.name = "checkout";
          checkoutInput.value = "Checkout";
          cartForm.appendChild(checkoutInput);
          HTMLFormElement.prototype.submit.call(cartForm); 
        });
      }
    });
  },

  initPickupRequiredModal() {
    const modal = document.getElementById("PickupRequiredModal");
    if (!modal || modal.dataset.aCartPickupRequiredInit === "1") return;

    modal.dataset.aCartPickupRequiredInit = "1";
    modal.classList.remove("is-active");
    modal.setAttribute("aria-hidden", "true");

    const closeModal = () => this.closePickupRequiredModal();
    document.getElementById("PickupRequiredModalButton")?.addEventListener("click", closeModal);
    modal.addEventListener("click", (event) => {
      if (event.target === modal) closeModal();
    });
    modal.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeModal();
    });
  },

  openPickupRequiredModal() {
    const modal = document.getElementById("PickupRequiredModal");
    if (!modal) return;

    modal.classList.add("is-active");
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("is-locked");
    document.getElementById("PickupRequiredModalButton")?.focus();
  },

  closePickupRequiredModal() {
    const modal = document.getElementById("PickupRequiredModal");
    if (!modal) return;

    modal.classList.remove("is-active");
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("is-locked");

    const firstEmptyField = ["pickup-area", "pickup-store", "pickup-date", "pickup-time"]
      .map((id) => document.getElementById(id))
      .find((field) => field && !field.value);
    firstEmptyField?.scrollIntoView({ behavior: "smooth", block: "center" });
    firstEmptyField?.focus({ preventScroll: true });
  },

  sortPickupStores() {
    const storeSelect = document.getElementById("pickup-store");
    if (!storeSelect) return;
    const options = Array.from(storeSelect.querySelectorAll('option:not([value=""])'));
    options.sort((a, b) => {
      const valA = parseInt(a.getAttribute("data-order") || 0, 10);
      const valB = parseInt(b.getAttribute("data-order") || 0, 10);
      return valA - valB;
    });
    options.forEach((opt) => storeSelect.appendChild(opt));
  },

  onPickupAreaChange() {
    const areaSelect = document.getElementById("pickup-area");
    const storeSelect = document.getElementById("pickup-store");
    if (!areaSelect || !storeSelect) return;

    this.ensurePickupMasterClones();
    const selectedAreaCd = (areaSelect.options[areaSelect.selectedIndex]?.getAttribute("data-area-cd") || "").trim();
    storeSelect.innerHTML = '<option value="">店舗を選択してください</option>';

    if (!selectedAreaCd) {
      this.renderPickupDates("");
      return;
    }
    if (!this._pickupStoreMasterClones) return;

    const cartItems = JSON.parse(document.getElementById("CartItemPickupData")?.textContent || "[]");
    const allowedStoreCodes = new Set();
    cartItems.forEach(item => {
      if (item.shop_codes && Array.isArray(item.shop_codes)) {
        item.shop_codes.forEach(code => {
          if (code) allowedStoreCodes.add(String(code).trim());
        });
      }
    });

    const activeStoreName = (this._initialSelectedStoreName || "").trim();
    const activeStoreCd = (this._initialSelectedStoreCd || "").trim();

    this._pickupStoreMasterClones.forEach((masterOption) => {
      const masterAreaCd = (masterOption.getAttribute("data-area") || "").trim();
      if (masterAreaCd !== selectedAreaCd) return;
      
      const storeCd = (masterOption.getAttribute("data-store-cd") || "").trim();
      if (!allowedStoreCodes.has(storeCd)) return;

      const option = masterOption.cloneNode(true);
      
      if ((activeStoreName && option.value.trim() === activeStoreName) || (activeStoreCd && storeCd === activeStoreCd)) {
        option.selected = true;
      }
      
      storeSelect.appendChild(option);
    });

    const selectedStoreCd = (storeSelect.options[storeSelect.selectedIndex]?.getAttribute("data-store-cd") || "").trim();
    const storeCdInput = document.getElementById("pickup-store-cd");
    if (storeCdInput && selectedStoreCd) {
      storeCdInput.value = selectedStoreCd;
    }

    this._initialSelectedStoreName = "";
    this._initialSelectedStoreCd = "";

    this.updateItemErrorStates(selectedStoreCd);
    this.renderPickupDates(selectedStoreCd);
    this.validatePickupRequired(true);
  },

  onPickupStoreChange() {
    const storeSelect = document.getElementById("pickup-store");
    const selectedStoreCd = storeSelect?.options[storeSelect.selectedIndex]?.getAttribute("data-store-cd") || "";
    const storeCdInput = document.getElementById("pickup-store-cd");
    if (storeCdInput) storeCdInput.value = (selectedStoreCd || "").trim();
    this.updateItemErrorStates(selectedStoreCd);
    this.renderPickupDates(selectedStoreCd);
    this.validatePickupRequired(true);
  },

  renderPickupDates(selectedStoreCd) {
    const dateSelect = document.getElementById("pickup-date");
    const container = document.getElementById("CartPickupSelector");
    if (!dateSelect || !container) return;

    const pickupItems = document.querySelectorAll('.a_cart__item[data-item-type="pickup"]');
    if (pickupItems.length === 0) return;

    const stopData = JSON.parse(document.getElementById("PickupStopData")?.textContent || "[]");
    const cartItems = JSON.parse(document.getElementById("CartItemPickupData")?.textContent || "[]");
    const actualStart = container.getAttribute("data-actual-start");
    const actualEnd = container.getAttribute("data-actual-end");
    if (!actualStart || !actualEnd) return;

    const currentSelectedDate = dateSelect.value;
    const areaSelect = document.getElementById("pickup-area");
    let selectedAreaCd = "";
    if (areaSelect && areaSelect.selectedIndex >= 0) {
      selectedAreaCd = areaSelect.options[areaSelect.selectedIndex]?.getAttribute("data-area-cd") || "";
    }

    let html = '<option value="">選択してください</option>';
    let curr = new Date(actualStart);
    let end = new Date(actualEnd);
    
    const jstToday = new Date(new Date().toLocaleString("en-US", {timeZone: "Asia/Tokyo"}));
    const todayStr = jstToday.getFullYear() + '-' + String(jstToday.getMonth() + 1).padStart(2, '0') + '-' + String(jstToday.getDate()).padStart(2, '0');

    const maxEnd = new Date(curr);
    maxEnd.setDate(maxEnd.getDate() + 21);
    if (end > maxEnd) end = maxEnd;

    while (curr <= end) {
      const dateStr = curr.toISOString().split("T")[0];
      if (dateStr > todayStr) {
        let isStoppedByProduct = false;
        let isStoppedByStoreOrArea = false;
        let stoppedProductNames = [];

        const dayStartObj = new Date(`${dateStr}T00:00:00+09:00`).getTime();
        const dayEndObj = new Date(`${dateStr}T23:59:59+09:00`).getTime();

        cartItems.forEach((item) => {
          if (
            stopData.find((s) => {
              const stopStartObj = new Date(s.start + "+09:00").getTime();
              const stopEndObj = new Date(s.end + "+09:00").getTime();
              
              const isFullDayStop = stopStartObj <= dayStartObj && stopEndObj >= dayEndObj;
              if (!isFullDayStop) return false;

              const hasShopLimit = s.shop_codes && s.shop_codes.length > 0;
              const hasAreaLimit = s.area_codes && s.area_codes.length > 0;
              let shopAreaMatched = true;
              if (hasShopLimit || hasAreaLimit) {
                shopAreaMatched = false;
                if (hasShopLimit && selectedStoreCd && s.shop_codes.includes(selectedStoreCd)) shopAreaMatched = true;
                if (hasAreaLimit && selectedAreaCd && s.area_codes.includes(selectedAreaCd)) shopAreaMatched = true;
              }
              if (!shopAreaMatched) return false;
              if (s.product_ids.length > 0 || s.collection_ids.length > 0) {
                 const pids = s.product_ids || [];
                 if (pids.includes(item.id)) return true;
                 const itemCols = item.collection_ids || [];
                 const stopCols = s.collection_ids || [];
                 return itemCols.some((cid) => stopCols.includes(cid));
              }
              return false;
            })
          ) {
            isStoppedByProduct = true;
            const productTitleEl = document.querySelector(`.a_cart__quantity-input[data-quantity-variant-id="${item.variant_id}"]`);
            const productTitle = productTitleEl ? productTitleEl.getAttribute("data-product-title") : "対象商品";
            if (productTitle && !stoppedProductNames.includes(productTitle)) {
              stoppedProductNames.push(productTitle);
            }
          }
        });

        if (selectedStoreCd || selectedAreaCd) {
          if (stopData.find((s) => {
            if (s.product_ids.length === 0 && s.collection_ids.length === 0) {
              const stopStartObj = new Date(s.start + "+09:00").getTime();
              const stopEndObj = new Date(s.end + "+09:00").getTime();
              const isFullDayStop = stopStartObj <= dayStartObj && stopEndObj >= dayEndObj;

              const isStoreStop = selectedStoreCd && s.shop_codes && s.shop_codes.includes(selectedStoreCd);
              const isAreaStop = selectedAreaCd && s.area_codes && s.area_codes.includes(selectedAreaCd);
              return (isStoreStop || isAreaStop) && isFullDayStop;
            }
            return false;
          })) {
            isStoppedByStoreOrArea = true;
          }
        }

        const dayLabel = ["日", "月", "火", "水", "木", "金", "土"][curr.getDay()];
        const displayDate = `${curr.getFullYear()}年${("0" + (curr.getMonth() + 1)).slice(-2)}月${("0" + curr.getDate()).slice(-2)}日(${dayLabel})`;
        const isSelected = dateStr === currentSelectedDate ? "selected" : "";
        
        const isAnyStopped = isStoppedByStoreOrArea || isStoppedByProduct;
        
        html += `<option value="${dateStr}" data-is-stopped="${isAnyStopped}" data-stopped-products="${stoppedProductNames.join(",")}" ${isSelected}>${displayDate}</option>`;
      }
      curr.setDate(curr.getDate() + 1);
    }
    dateSelect.innerHTML = html;
  },

  updateItemErrorStates(selectedStoreCd) {
    const pickupItems = document.querySelectorAll(".a_cart__item[data-item-type='pickup']");
    const consistencyMsg = document.getElementById("StoreErrorMsg");
    let anyError = false;
    pickupItems.forEach((row) => {
      if (selectedStoreCd === "") {
        row.classList.remove("is-error");
        return;
      }
      const raw = row.getAttribute("data-shop-codes") || "";
      const allowed = raw.split(",").map((c) => c.trim()).filter(Boolean);
      
      if (allowed.length === 0) {
        row.classList.add("is-error");
        anyError = true;
        return;
      }
      
      const isError = !allowed.includes(selectedStoreCd);
      row.classList.toggle("is-error", isError);
      if (isError) anyError = true;
    });
    if (consistencyMsg) consistencyMsg.style.display = anyError ? "block" : "none";
    this.refreshCheckoutButton();
  },

  updateAllStates() {
    this.validateAllItems();
    this.syncCheckoutUiToCartRows();
    this.checkPickupDateConsistency();
    this.validateNote();
    this.refreshCheckoutButton();
  },

  syncCheckoutUiToCartRows() {
    const items = document.querySelectorAll(".a_cart__item");
    const errorMsg = document.getElementById("MixedRouteErrorMessage");
    const loginAndTermsGroup = document.querySelector(".a_cart__login-and-terms");
    const noLoginArea = document.querySelector(".a_cart__no-login");
    const agreeArea = document.querySelector(".a_cart__pickup-agree");
    const checkoutButton = document.querySelector("#checkout");
    const checkoutText = document.querySelector("#CheckoutText");
    const mixedFlagInput = document.getElementById("MixedRouteFlag");
    const loggedIn = cartPageIsCustomerLoggedIn();

    const isMixed = mixedFlagInput ? mixedFlagInput.value === "true" : false;

    const types = new Set();
    items.forEach((item) => {
      const type = item.getAttribute("data-item-type");
      if (type) types.add(type);
    });

    const hasPickup = types.has("pickup");
    const hasDelivery = types.has("delivery");
    const hasSubscription = types.has("subscription");
    const ageCtx = document.getElementById("CartAgeGateContext")?.value?.trim() || "none";

    if (errorMsg) errorMsg.style.display = isMixed ? "block" : "none";
    if (loginAndTermsGroup) loginAndTermsGroup.style.display = !isMixed && items.length > 0 ? "block" : "none";

    if (!isMixed && items.length > 0) {
      if (hasPickup || hasSubscription || ageCtx === "guest_alcohol") {
        // 店舗受取・定期便・酒類ゲスト時は、上段エリア・同意欄を隠す
        if (noLoginArea) noLoginArea.style.display = "none";
        if (agreeArea) agreeArea.style.display = "none";
        if (checkoutButton && checkoutText) {
          checkoutText.innerText = loggedIn ? "ご注文手続きへ進む" : (hasPickup ? "ログインして注文" : "ログインして購入");
        }
        checkoutButton?.classList.remove("a_cart__checkout-button--no-member");
      } else if (hasDelivery) {
        // 通常発送商品（ゲスト時）は上段テキストを表示＋同意欄を表示
        if (noLoginArea) noLoginArea.style.display = loggedIn ? "none" : "block";
        if (agreeArea) agreeArea.style.display = loggedIn ? "none" : "block";
        if (checkoutText) checkoutText.innerText = loggedIn ? "ご注文手続きへ進む" : "会員登録せずに購入";
        if (checkoutButton) {
          if (!loggedIn) checkoutButton.classList.add("a_cart__checkout-button--no-member");
          else checkoutButton.classList.remove("a_cart__checkout-button--no-member");
        }
      }
    }
    this.refreshCheckoutButton();
  },

  checkPickupDateConsistency() {
    const pickupItems = document.querySelectorAll('.a_cart__item[data-item-type="pickup"]');
    const allItems = document.querySelectorAll(".a_cart__item");
    const mismatchEl = document.getElementById("PickupDateMismatchError");
    const timeMismatchEl = document.getElementById("PickupTimeMismatchError");
    const periodClosedEl = document.getElementById("PickupPeriodClosedError");
    const selectorContainer = document.getElementById("CartPickupSelector");
    const stopErrorMsgEl = document.getElementById("PickupStopErrorMsg");
    const stopErrorTextEl = document.getElementById("PickupStopErrorText");
    
    const areaSelect = document.getElementById("pickup-area");
    const storeSelect = document.getElementById("pickup-store");
    const dateSelect = document.getElementById("pickup-date");
    const timeSelect = document.getElementById("pickup-time");

    if (!mismatchEl || !selectorContainer) return false;

    const isMixed = document.getElementById("MixedRouteFlag")?.value === "true";
    const hasNonPickupItems = Array.from(allItems).some((item) => item.getAttribute("data-item-type") !== "pickup");
    const actualStart = (selectorContainer.getAttribute("data-actual-start") || "").trim();
    const actualEnd = (selectorContainer.getAttribute("data-actual-end") || "").trim();
    
    const domPeriodClosedFlag = document.getElementById("PickupPeriodClosedFlag")?.value === "true";

    let isDataMismatch = false;
    let periodClosedByDom = domPeriodClosedFlag;

    if (pickupItems.length > 0 && actualStart && actualEnd) {
      if (actualStart > actualEnd) {
        if (pickupItems.length > 1) isDataMismatch = true;
        else periodClosedByDom = true;
      }
    }

    let isStopDateSelected = false;
    
    if (dateSelect && dateSelect.selectedIndex > 0) {
      const selectedOption = dateSelect.options[dateSelect.selectedIndex];
      const selectedDate = selectedOption.value; 
      let selectedTime = null;
      let checkTimestamp = null;
      
      if (timeSelect && timeSelect.selectedIndex > 0) {
        let rawTime = timeSelect.value.split('-')[0].trim();
        
        if (rawTime.length === 4) { 
          rawTime = "0" + rawTime;
        }
        
        selectedTime = rawTime;
        checkTimestamp = new Date(`${selectedDate}T${selectedTime}+09:00`).getTime();
      }

      if (selectedOption.getAttribute("data-is-stopped") === "true") {
        isStopDateSelected = true;
        const stoppedProducts = selectedOption.getAttribute("data-stopped-products");
        if (stopErrorTextEl) {
          if (stoppedProducts) stopErrorTextEl.textContent = `「${stoppedProducts}」は受け取り停止期間中のため、ご購入いただけません。`;
          else stopErrorTextEl.textContent = `選択された受取店舗・日時は受け取り停止期間中のため、ご購入いただけません。`;
        }
      } 
      else if (checkTimestamp !== null) {
        const stopData = JSON.parse(document.getElementById("PickupStopData")?.textContent || "[]");
        const cartItems = JSON.parse(document.getElementById("CartItemPickupData")?.textContent || "[]");
        const selectedStoreCd = storeSelect?.options[storeSelect.selectedIndex]?.getAttribute("data-store-cd") || "";
        const selectedAreaCd = areaSelect?.options[areaSelect.selectedIndex]?.getAttribute("data-area-cd") || "";

        stopData.forEach((s) => {
          const stopStartObj = new Date(s.start + "+09:00").getTime();
          const stopEndObj = new Date(s.end + "+09:00").getTime();
          
          if (checkTimestamp >= stopStartObj && checkTimestamp <= stopEndObj) {
            
            const hasShopLimit = s.shop_codes && s.shop_codes.length > 0;
            const hasAreaLimit = s.area_codes && s.area_codes.length > 0;
            let shopAreaMatched = true;
            if (hasShopLimit || hasAreaLimit) {
              shopAreaMatched = false;
              if (hasShopLimit && selectedStoreCd && s.shop_codes.includes(selectedStoreCd)) shopAreaMatched = true;
              if (hasAreaLimit && selectedAreaCd && s.area_codes.includes(selectedAreaCd)) shopAreaMatched = true;
            }
            if (!shopAreaMatched) return;

            if (s.product_ids.length === 0 && s.collection_ids.length === 0) {
              isStopDateSelected = true;
              if (stopErrorTextEl) stopErrorTextEl.textContent = `選択された受取日時は受け取り停止期間中のため、ご購入いただけません。`;
            } else {
              let stoppedProductsList = [];
              cartItems.forEach((item) => {
                const pids = s.product_ids || [];
                const itemCols = item.collection_ids || [];
                const stopCols = s.collection_ids || [];
                if (pids.includes(item.id) || itemCols.some(cid => stopCols.includes(cid))) {
                  const productTitleEl = document.querySelector(`.a_cart__quantity-input[data-quantity-variant-id="${item.variant_id}"]`);
                  const productTitle = productTitleEl ? productTitleEl.getAttribute("data-product-title") : "対象商品";
                  stoppedProductsList.push(productTitle);
                }
              });
              if (stoppedProductsList.length > 0) {
                isStopDateSelected = true;
                if (stopErrorTextEl) stopErrorTextEl.textContent = `「${stoppedProductsList.join(",")}」は選択された日時の受け取りを停止しているため、ご購入いただけません。`;
              }
            }
          }
        });
      }
    }

    mismatchEl.style.display = !isMixed && isDataMismatch ? "block" : "none";
    const timeMismatchVisible = !!timeMismatchEl && window.getComputedStyle(timeMismatchEl).display !== "none";
    
    if (periodClosedEl) periodClosedEl.style.display = isMixed || isDataMismatch ? "none" : periodClosedByDom ? "block" : "none";
    if (stopErrorMsgEl) stopErrorMsgEl.style.display = (!isMixed && !isDataMismatch && !periodClosedByDom && isStopDateSelected) ? "block" : "none";

    const showPickupUi = !isMixed && !isDataMismatch && !periodClosedByDom && !timeMismatchVisible && !hasNonPickupItems && pickupItems.length > 0;
    selectorContainer.style.display = showPickupUi ? "block" : "none";

    const loginAndTermsGroup = document.querySelector(".a_cart__login-and-terms");
    if (loginAndTermsGroup) {
      const mismatchVisible = mismatchEl.style.display === "block";
      const periodVisible = periodClosedEl?.style.display === "block";
      loginAndTermsGroup.style.display = !isMixed && allItems.length > 0 && !mismatchVisible && !timeMismatchVisible && !periodVisible && !isStopDateSelected ? "block" : "none";
    }
    
    return isDataMismatch || isStopDateSelected || periodClosedByDom;
  },

  validateCartRow(input) {
    if (!input) return;
    const idx = input.getAttribute("data-index");
    if (!idx) return;

    const limitRaw = input.getAttribute("data-upper-limit");
    const limit = limitRaw === "" || limitRaw == null ? NaN : parseInt(String(limitRaw).trim(), 10);
    const val = parseInt(String(input.value).trim(), 10);
    const hasCap = Number.isFinite(limit) && limit >= 0;
    const over = hasCap && Number.isFinite(val) && val > limit;
    const showLimitNotice = hasCap && limit > 0 && Number.isFinite(val) && val > 0 && val >= limit;

    const mobileEl = document.getElementById(`Quantity-Error-Mobile-${idx}`);
    const desktopEl = document.getElementById(`Quantity-Error-Desktop-${idx}`);
    const title = input.getAttribute("data-product-title") || "";

    const syncLimitNotice = (el, show, isOver) => {
      if (!el) return;
      el.style.display = show ? "block" : "none";
      if (show && hasCap) {
        const titleSpan = el.querySelector(".error-product-title");
        const limitSpan = el.querySelector(".error-upper-limit");
        if (titleSpan) titleSpan.textContent = title;
        if (limitSpan) limitSpan.textContent = String(limit);
      }
      const overMsg = el.querySelector("[data-quantity-limit-over-message]");
      if (overMsg) overMsg.style.display = show && isOver ? "inline" : "none";
    };

    syncLimitNotice(mobileEl, showLimitNotice, over);
    syncLimitNotice(desktopEl, showLimitNotice, over);

    const row = input.closest("tr");
    if (row) {
      row.querySelector(".cart-item__totals")?.classList.toggle("is-quantity-over-limit", showLimitNotice);
      row.querySelector(".a_cart__delivery-options--sp")?.classList.toggle("is-quantity-over-limit", showLimitNotice);
    }

    const plusBtn = input.closest("quantity-input")?.querySelector('button[name="plus"]');
    if (plusBtn) {
      if (over) {
        plusBtn.disabled = true;
        plusBtn.dataset.aCartLimitLock = "true";
      } else if (plusBtn.dataset.aCartLimitLock === "true" && !input.disabled) {
        plusBtn.disabled = false;
        delete plusBtn.dataset.aCartLimitLock;
      }
    }
    if (over) input.setAttribute("data-has-error", "true");
    else input.removeAttribute("data-has-error");
  },

  validateAllItems() {
    document.querySelectorAll(".a_cart__quantity-input").forEach((input) => this.validateCartRow(input));
  },

  refreshCheckoutButton() {
    const hasQuantityLimitError = !!document.querySelector('.a_cart__input[data-has-error="true"]');
    const checkoutButton = document.querySelector("#checkout");
    const loginCheckoutButton = document.getElementById("CartLoginCheckoutButton");
    const ageCtx = document.getElementById("CartAgeGateContext")?.value?.trim() || "none";
    const hasMixedRouteError = document.getElementById("MixedRouteFlag")?.value === "true";
    const loggedIn = cartPageIsCustomerLoggedIn();
    const hasPickupFlag = document.getElementById("CartHasPickupFlag")?.value === "true";
    const hasDeliveryFlag = document.getElementById("CartHasDeliveryFlag")?.value === "true";
    const hasSubscriptionFlag = document.getElementById("CartHasSubscriptionFlag")?.value === "true";
    const hasEgiftFlag = document.getElementById("CartHasEgiftFlag")?.value === "true";
    const isGuestPickupAlcoholOrSubscription =
      !loggedIn &&
      (hasPickupFlag || ageCtx === "guest_alcohol" || hasSubscriptionFlag);

    if (loginCheckoutButton) {
      const shouldDisableLogin =
        hasMixedRouteError || !isGuestPickupAlcoholOrSubscription;

      const loginUrl = loginCheckoutButton.dataset.loginUrl;

      loginCheckoutButton.classList.toggle("is-disabled", shouldDisableLogin);

      if (shouldDisableLogin) {
        loginCheckoutButton.removeAttribute("href");
        loginCheckoutButton.setAttribute("aria-disabled", "true");
        loginCheckoutButton.setAttribute("tabindex", "-1");
      } else {
        if (loginUrl) loginCheckoutButton.setAttribute("href", loginUrl);
        loginCheckoutButton.removeAttribute("aria-disabled");
        loginCheckoutButton.removeAttribute("tabindex");
      }
    }
    if (checkoutButton) {
      checkoutButton.hidden = false;
    }

    const alcoholGuestEl = document.getElementById("AlcoholGuestError");
    const alcoholBirthEl = document.getElementById("AlcoholBirthError");
    
    if (alcoholGuestEl) alcoholGuestEl.style.display = ageCtx === "guest_alcohol" ? "block" : "none";
    if (alcoholBirthEl) alcoholBirthEl.style.display = ageCtx === "minor_alcohol" ? "block" : "none";

    if (!checkoutButton) return;

    const cartRows = document.querySelectorAll(".a_cart__item");
    const hasPickup = Array.from(cartRows).some((i) => i.getAttribute("data-item-type") === "pickup");
    const hasSubscription = Array.from(cartRows).some((i) => i.getAttribute("data-item-type") === "subscription");

    const hasStoreError = !!document.querySelector(".a_cart__item.is-error");
    const hasPickupDateMismatch = document.getElementById("PickupDateMismatchError")?.style.display === "block";
    const pickupTimeMismatchEl = document.getElementById("PickupTimeMismatchError");
    const hasPickupTimeMismatch = !!pickupTimeMismatchEl && window.getComputedStyle(pickupTimeMismatchEl).display !== "none";
    
    const hasPickupPeriodClosed = document.getElementById("PickupPeriodClosedFlag")?.value === "true";
    
    const hasPickupStopError = document.getElementById("PickupStopErrorMsg")?.style.display === "block";

    const agreeCheckbox = document.querySelector("#cart-agree");
    const isTermsAgreed = agreeCheckbox ? agreeCheckbox.checked : true;
    const privacyCheckbox = document.querySelector("#cart-privacy-agree");
    const isPrivacyAgreed = privacyCheckbox ? privacyCheckbox.checked : true;

    const isFatalError = hasQuantityLimitError || hasStoreError || hasPickupDateMismatch || hasPickupTimeMismatch || hasPickupPeriodClosed || hasPickupStopError || hasMixedRouteError || (ageCtx === "minor_alcohol") || this._noteError;

    // 未選択チェック：通常発送商品で規約同意が必要な時だけ isTermsUnfilled を有効にする
    const isTermsRequired = !loggedIn && !hasPickup && !hasSubscription && (ageCtx !== "guest_alcohol");
    const isTermsUnfilled = isTermsRequired && (!isTermsAgreed || !isPrivacyAgreed);
    const isPickupRequiredUnfilled = hasPickup && (!loggedIn || !this.validatePickupRequired(false, false));

    const shouldDisableButton = isFatalError || isTermsUnfilled || isPickupRequiredUnfilled;

    const loginAndTerms = document.querySelector(".a_cart__login-and-terms");
    if (loginAndTerms) loginAndTerms.classList.toggle("is-fatal-checkout-block", isFatalError);

    const cartBlocks = document.querySelector(".cart__blocks");
    if (cartBlocks) cartBlocks.classList.toggle("has-fatal-error", isFatalError);

    const checkoutTextElement = document.querySelector("#CheckoutText");
    if (checkoutTextElement) {
      if (isFatalError) {
        checkoutTextElement.innerText = "購入できません";
      } else {
        if (!loggedIn && hasPickup) {
          checkoutTextElement.innerText = "ログインして注文";
        } else if (!loggedIn && (hasSubscription || ageCtx === "guest_alcohol")) {
          checkoutTextElement.innerText = "ログインして購入";
        } else if (!loggedIn && !hasPickup && !hasSubscription && ageCtx !== "guest_alcohol") {
          checkoutTextElement.innerText = "会員登録せずに購入";
        } else {
          checkoutTextElement.innerText = "ご注文手続きへ進む";
        }
      }
    }

    checkoutButton.disabled = shouldDisableButton;
    checkoutButton.style.opacity = shouldDisableButton ? "0.5" : "1";
    checkoutButton.style.pointerEvents = shouldDisableButton ? "none" : "auto";
    checkoutButton.style.backgroundColor = shouldDisableButton ? "#A8A8A8" : "";
  },

  validateNote() {
    const notes = document.querySelectorAll(".a_cart__note-textarea");
    let hasError = false;
    notes.forEach((t) => {
      if (t.value.length > 200) hasError = true;
      t.style.borderColor = t.value.length > 200 ? "#d00000" : "";
    });
    document.querySelectorAll(".a_cart__note-error").forEach((m) => (m.style.display = hasError ? "block" : "none"));
    this._noteError = hasError;
  },

  validatePickupRequired(isUserAction = true, forceShowAll = false) {
    const selector = document.getElementById("CartPickupSelector");
    if (!selector || selector.style.display === "none") return true;

    const loggedIn = cartPageIsCustomerLoggedIn();
    if (!loggedIn) return true;

    const errorEl = document.getElementById("PickupRequiredError");
    const area = document.getElementById("pickup-area")?.value ?? "";
    const store = document.getElementById("pickup-store")?.value ?? "";
    const date = document.getElementById("pickup-date")?.value ?? "";
    const time = document.getElementById("pickup-time")?.value ?? "";

    const messages = [];
    if (forceShowAll) {
      if (!area) messages.push("店舗エリアは必ず入力してください。");
      if (!store) messages.push("店舗は必ず入力してください。");
      if (!date) messages.push("受取日は必ず入力してください。");
      if (!time) messages.push("受取時間は必ず入力してください。");
    } else if (isUserAction) {
      if (!area && (store || date || time)) messages.push("店舗エリアは必ず入力してください。");
      if (!store && (date || time)) messages.push("店舗は必ず入力してください。");
      if (!date && time) messages.push("受取日は必ず入力してください。");
    }

    if (errorEl) {
      errorEl.innerHTML = messages.map((msg) => `<p>${msg}</p>`).join("");
      errorEl.style.display = messages.length > 0 ? "block" : "none";
    }
    return !!(area && store && date && time);
  },

  async clearPickupAttributesIfNoPickupItems() {
    try {
      const pickupItems = document.querySelectorAll('.a_cart__item[data-item-type="pickup"]');
      
      if (pickupItems.length === 0) {
        const attributes = {
          '受取エリア': '',
          '受け取り店舗': '',
          '受取店舗コード': '',
          '受け取り希望日': '',
          '受け取り希望時間': '',
          '店舗受取': ''
        };
        
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes })
        });
      }
    } catch (err) {
      console.error('Failed to clear pickup attributes:', err);
    }
  },

  bindDocumentEvents() {
    if (this._documentEventsBound) return;
    this._documentEventsBound = true;

    document.addEventListener("change", (e) => {
      const t = e.target;
      if (t.classList?.contains("a_cart__quantity-input") || t.id === "cart-agree" || t.id === "cart-privacy-agree") {
        this.updateAllStates();
      }
      if (t.id === "pickup-area") {
        this.onPickupAreaChange();
        this.validatePickupRequired(true, false);
        this.updateAllStates();
      }
      if (t.id === "pickup-store") {
        this.onPickupStoreChange();
        this.validatePickupRequired(true, false);
        this.updateAllStates();
      }
      if (t.id === "pickup-date" || t.id === "pickup-time") {
        this.validatePickupRequired(true, false);
        this.updateAllStates();
      }
    });

    document.addEventListener("click", (e) => {

      // 1. 複数配送先へ送るボタンがクリックされた時
      const multiAddrBtn = e.target.closest?.(".btn_multi_address a");
      if (multiAddrBtn) {
        const ctx = document.getElementById("CartAgeGateContext")?.value?.trim();
        if (
          ctx === "modal_ok" &&
          !this._ageGateCheckoutConfirmed &&
          document.getElementById("AgeGateModal")
        ) {
          e.preventDefault();
          this._ageGateTargetUrl = multiAddrBtn.getAttribute("href");
          const modal = document.getElementById("AgeGateModal");
          modal?.classList.add("is-active");
          document.body.classList.add("is-locked");
          return;
        }
      }

      // 2. 通常購入ボタンがクリックされた時
      const checkoutBtn = e.target.closest?.("#checkout");
      if (!checkoutBtn) return;

      this._ageGateTargetUrl = null;

      const ok = this.validatePickupRequired(true, true);
      if (!ok) {
        e.preventDefault();
        this.openPickupRequiredModal();
        return;
      }

      const loggedIn = cartPageIsCustomerLoggedIn();
      const cartRows = document.querySelectorAll(".a_cart__item");
      const hasPickup = Array.from(cartRows).some((i) => i.getAttribute("data-item-type") === "pickup");
      const hasSubscription = Array.from(cartRows).some((i) => i.getAttribute("data-item-type") === "subscription");
      const ctx = document.getElementById("CartAgeGateContext")?.value?.trim();

      // 店舗受取、定期便、酒類（guest_alcohol）で非ログイン時はログイン画面へ飛ばす
      if ((hasPickup || hasSubscription || ctx === "guest_alcohol") && !loggedIn) {
        e.preventDefault();
        const loginUrl = checkoutBtn.getAttribute("data-login-url") || "/customer_authentication/login";
        window.location.href = loginUrl;
        return;
      }

      // ログイン済みかつ年齢制限商品があり、年齢確認がまだの場合はモーダルを起動
      if (
        ctx === "modal_ok" &&
        !this._ageGateCheckoutConfirmed &&
        document.getElementById("AgeGateModal") &&
        !checkoutBtn.disabled
      ) {
        e.preventDefault();
        const modal = document.getElementById("AgeGateModal");
        modal?.classList.add("is-active");
        document.body.classList.add("is-locked");
        return;
      }

      e.preventDefault(); 
      checkoutBtn.disabled = true;

      const checkoutTextSpan = checkoutBtn.querySelector("#CheckoutText");
      if (checkoutTextSpan) {
        checkoutTextSpan.innerText = "処理中...";
      } else {
        checkoutBtn.innerText = "処理中...";
      }

      saveFlowAttributesViaAPI().then(() => {
        const cartForm = document.getElementById("cart");
        if (cartForm) {
          const checkoutInput = document.createElement("input");
          checkoutInput.type = "hidden";
          checkoutInput.name = "checkout";
          checkoutInput.value = "Checkout";
          cartForm.appendChild(checkoutInput);
          HTMLFormElement.prototype.submit.call(cartForm); 
        }
      });
    });

    document.addEventListener("focusout", (e) => {
      const t = e.target;
      if (!t.classList?.contains("a_cart__quantity-input")) return;
      if (t.getAttribute("data-has-error") !== "true") return;
      const prev = this._quantityLimitBlurResyncTimers.get(t);
      if (prev) clearTimeout(prev);
      const base = typeof ON_CHANGE_DEBOUNCE_TIMER === "number" ? ON_CHANGE_DEBOUNCE_TIMER : 300;
      const id = setTimeout(() => {
        this._quantityLimitBlurResyncTimers.delete(t);
        this.validateAllItems();
        this.refreshCheckoutButton();
      }, base + 50);
      this._quantityLimitBlurResyncTimers.set(t, id);
    });

    document.addEventListener("input", (e) => {
      const t = e.target;
      if (t.classList?.contains("a_cart__quantity-input")) {
        this.validateCartRow(t);
        this.refreshCheckoutButton();
        return;
      }
      if (!t.classList?.contains("a_cart__note-textarea")) return;
      document.querySelectorAll(".a_cart__note-textarea").forEach((n) => {
        if (n !== e.target) n.value = e.target.value;
      });
      this.validateNote();
      this.refreshCheckoutButton();
    });
  },
};

// =============================================================================
// 起動
// =============================================================================

if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === "cart-items") CartManager.onCartItemsAjaxDone();
  });
}

CartManager.init();
document.addEventListener("shopify:section:load", () => CartManager.init());

window.addEventListener("pageshow", () => {
  try {
    CartManager.updateAllStates();
  } catch (_) {
    CartManager.refreshCheckoutButton?.();
  }
});
